import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output, inject } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CanvasDocumentView, CanvasGroup } from './canvas.models';
import { DocumentCardComponent } from './document-card.component';
import { PreviewDocument } from '../common/service/document.service';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';

@Component({
  selector: 'app-document-group',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DragDropModule, FormsModule, MatButtonModule, MatIconModule, DocumentCardComponent],
  template: `
    <div class="group-shell">
      <section class="group" [attr.data-canvas-group-id]="group.id" [class.group--drop-target]="dropActive">
        <header class="group__header" (pointerdown)="startGroupDrag($event)">
          <button class="group__drag-handle" type="button" aria-label="Move group">
            <span class="group__drag-dots" aria-hidden="true">
              <span></span><span></span><span></span><span></span><span></span><span></span>
            </span>
          </button>
          <input
            class="group__title"
            [ngModel]="group.title"
            (ngModelChange)="rename.emit($event)"
            aria-label="Group name"
          />
          @if (selectionMode) {
            <label class="group__select" (pointerdown)="$event.stopPropagation()" (click)="$event.stopPropagation()">
              <input
                type="checkbox"
                [checked]="groupSelected"
                [indeterminate]="groupPartial && !groupSelected"
                (change)="groupSelectionChange.emit(eventChecked($event))"
              />
            </label>
          }
          <span class="group__count">{{ documents.length }}</span>
          <button
            mat-icon-button
            type="button"
            class="delete_group_btn"
            aria-label="Delete group"
            [disabled]="readOnly()"
            (click)="delete.emit()"
          >
            <mat-icon>close</mat-icon>
          </button>
        </header>
        <div
          class="group__body"
          cdkDropList
          [cdkDropListSortingDisabled]="true"
          [cdkDropListAutoScrollDisabled]="true"
          [cdkDropListEnterPredicate]="dropListEnterPredicate"
          [id]="dropListId"
          [cdkDropListData]="group"
          [cdkDropListConnectedTo]="connectedDropListIds"
          [cdkDropListDisabled]="readOnly()"
          (cdkDropListDropped)="groupDrop.emit($event)"
          (cdkDropListEntered)="groupEntered.emit(group.id)"
          (cdkDropListExited)="groupExited.emit(group.id)"
        >
          <div class="group__document-wrap" *ngFor="let document of documents; trackBy: trackByDocumentId">
            <app-document-card
              [document]="document"
              variant="canvas"
              [previewSrc]="previewSrcFor(document)"
              [cdkDragData]="document"
              [cdkDragBoundary]="'.canvas__stage'"
              [cdkDragPreviewClass]="'canvas-document-drag-preview'"
              cdkDragHandle
              (cdkDragMoved)="documentDragMoved.emit($event)"
              (deleteDocument)="removeDocument.emit(document.placementId)"
              (cdkDragEnded)="moveDocument.emit($event)"
            >
            </app-document-card>
            @if (selectionMode) {
              <label class="group__document-select" (click)="$event.stopPropagation()">
                <input
                  type="checkbox"
                  [checked]="selectedDocumentIds.has(document.id)"
                  (change)="documentSelectionChange.emit({ id: document.id, selected: eventChecked($event) })"
                />
              </label>
            }
          </div>
          <p *ngIf="!documents.length" class="group__empty">Drop documents here</p>
        </div>
      </section>
      @if (dropActive) {
        <div class="group__drop-indicator" aria-hidden="true">
          <mat-icon>south</mat-icon>
          <span>Drop into {{ group.title }}</span>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        position: absolute;
        z-index: 2;
        width: fit-content;
      }
      .group-shell {
        position: relative;
        overflow: visible;
        width: fit-content;
      }
      .group {
        position: relative;
        width: 100%;
        height: 100%;
        min-width: 360px;
        max-width: 360px;
        border: 1px solid #cbd5e1;
        border-radius: 12px;
        background: #ffffff;
        box-shadow: 0 6px 24px rgba(15, 23, 42, 0.08);
        overflow: hidden;
      }
      .group.group--drop-target {
        border-color: #0f766e;
        box-shadow:
          0 0 0 3px rgba(15, 118, 110, 0.22),
          0 10px 28px rgba(15, 118, 110, 0.18);
      }
      .group__header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px;
        border-bottom: 1px solid #e5e7eb;
        background: #f8fafc;
        user-select: none;
        cursor: grab;
      }
      .group__select,
      .group__document-select {
        position: absolute;
        z-index: 5;
      }
      .group__document-wrap {
        position: relative;
        min-width: 0;
      }
      .group__select {
        position: static;
      }
      .group__document-select {
        top: 8px;
        left: 8px;
      }
      .group__document-select input {
        width: 16px;
        height: 16px;
        accent-color: #476873;
        cursor: pointer;
      }
      .group__select input {
        width: 16px;
        height: 16px;
        accent-color: #e3b657;
        cursor: pointer;
      }
      .group__header:active {
        cursor: grabbing;
      }
      .group__drag-handle {
        display: inline-flex;
        flex: 0 0 24px;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #94a3b8;
        cursor: grab;
      }
      .group__drag-handle:active {
        cursor: grabbing;
      }
      .group__drag-handle:hover {
        background: #e2e8f0;
        color: #475569;
      }
      .group__drag-dots {
        display: grid;
        grid-template-columns: repeat(2, 3px);
        grid-auto-rows: 3px;
        gap: 3px;
      }
      .group__drag-dots span {
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background: currentColor;
      }
      .group__title {
        flex: 1 1 auto;
        border: 0;
        background: transparent;
        font-size: 14px;
        font-weight: 600;
        outline: none;
        min-width: 0;
      }
      .group__count {
        font-size: 12px;
        font-weight: 600;
        color: #475569;
        background: #e2e8f0;
        border-radius: 999px;
        padding: 4px 8px;
      }
      .group__body {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
        padding: 12px 12px 4px 4px;
        min-height: 0;
        height: calc(100% - 87px);
        align-content: start;
        overflow-y: auto;
        overflow-x: hidden;
        background: linear-gradient(180deg, rgba(248, 250, 252, 0.9), rgba(255, 255, 255, 0.95));
      }
      .group__empty {
        margin: 0;
        padding: 20px 8px;
        color: #94a3b8;
        font-size: 12px;
        text-align: center;
      }
      .group__drop-indicator {
        position: absolute;
        left: 50%;
        top: -34px;
        transform: translate(-50%, 0);
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border-radius: 999px;
        background: linear-gradient(180deg, rgba(15, 118, 110, 0.16), rgba(15, 118, 110, 0.12));
        color: #0f766e;
        font-size: 12px;
        font-weight: 600;
        pointer-events: none;
        box-shadow: 0 8px 18px rgba(15, 118, 110, 0.12);
        z-index: 4;
        animation: group-drop-bounce 900ms ease-in-out infinite;
        white-space: nowrap;
      }
      .group__drop-indicator mat-icon {
        width: 16px;
        height: 16px;
        font-size: 16px;
        line-height: 16px;
        animation: group-icon-pulse 900ms ease-in-out infinite;
      }
      @keyframes group-drop-bounce {
        0%,
        100% {
          transform: translate(-50%, 0) translateY(0);
          opacity: 0.85;
        }
        50% {
          transform: translate(-50%, 0) translateY(-5px);
          opacity: 1;
        }
      }
      @keyframes group-icon-pulse {
        0%,
        100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-1px);
        }
      }
      :host ::ng-deep .group__body .document-card.canvas {
        position: relative;
        width: 100%;
        max-width: none;
        min-width: 0;
        padding-right: 8px;
        box-sizing: border-box;
      }
      :host ::ng-deep .group__body .document-card.canvas {
        top: -4px;
        right: -4px;
      }
      .delete_group_btn.mat-mdc-icon-button {
        --mat-icon-button-state-layer-size: 32px;
        padding: 4px;

        mat-icon {
          font-size: 1.4rem;
          width: 1.4rem;
          height: 1.4rem;
        }
      }
    `,
  ],
})
export class DocumentGroupComponent {
  /**
   * Mirrors the server's `Company.mayWrite` — see `CompanyWriteAccess`. Read directly rather than
   * passed down: this component sits several levels below the page that knows the company.
   */
  readonly readOnly = inject(CompanyWriteAccess).readOnly;

  @Input({ required: true }) group!: CanvasGroup;
  @Input() documents: CanvasDocumentView[] = [];
  @Input() previewMap: Record<string, PreviewDocument> = {};
  @Input() dropActive = false;
  @Input() selectionMode = false;
  @Input() groupSelected = false;
  @Input() groupPartial = false;
  @Input() selectedDocumentIds = new Set<string>();
  @Input() dropListId = '';
  @Input() connectedDropListIds: string[] = [];
  @Input() dropListEnterPredicate: (drag: { data: unknown }) => boolean = () => true;
  @Output() rename = new EventEmitter<string>();
  @Output() delete = new EventEmitter<void>();
  @Output() groupDrop = new EventEmitter<any>();
  @Output() moveDocument = new EventEmitter<any>();
  @Output() documentDragMoved = new EventEmitter<any>();
  @Output() removeDocument = new EventEmitter<string>();
  @Output() groupEntered = new EventEmitter<string>();
  @Output() groupExited = new EventEmitter<string>();
  @Output() groupSelectionChange = new EventEmitter<boolean>();
  @Output() documentSelectionChange = new EventEmitter<{ id: string; selected: boolean }>();
  eventChecked(event: Event) {
    return (event.target as HTMLInputElement).checked;
  }
  @Output() groupPointerStarted = new EventEmitter<PointerEvent>();
  @Output() groupPointerMoved = new EventEmitter<PointerEvent>();
  @Output() groupPointerEnded = new EventEmitter<PointerEvent>();
  private groupPointerId: number | null = null;

  previewSrcFor(document: CanvasDocumentView) {
    return this.previewMap[document.id]?.preview ? `data:image/png;base64,${this.previewMap[document.id].preview}` : '';
  }

  trackByDocumentId(_: number, document: CanvasDocumentView) {
    return document.placementId;
  }

  startGroupDrag(event: PointerEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, button:not(.group__drag-handle), [contenteditable="true"]')) {
      return;
    }
    event.preventDefault();
    this.groupPointerId = event.pointerId;
    this.groupPointerStarted.emit(event);
  }

  @HostListener('document:pointermove', ['$event'])
  handlePointerMove(event: PointerEvent) {
    if (event.pointerId === this.groupPointerId) {
      this.groupPointerMoved.emit(event);
    }
  }

  @HostListener('document:pointerup', ['$event'])
  handlePointerUp(event: PointerEvent) {
    if (event.pointerId === this.groupPointerId) {
      this.groupPointerId = null;
      this.groupPointerEnded.emit(event);
    }
  }
}
